import React, { useMemo } from 'react';
import { ColumnHeaderProvider, ColumnHeader, TableFilterItem, calculateFilterCount } from 'src/presentations/components/molecules/table/columnHeader';
import { useTourContext } from 'src/presentations/guided-tours';
import { createFilterOptions } from './rateCardCharges/filterUtils';

interface ContractManagerColumnHeaderProps {
  column: any;
  accessorKey: string;
  customHeader?: React.ReactNode;
  originalData?: any[]; // All data for generating filter options (optional if filterOptions provided)
  filterOptions?: TableFilterItem[]; // Pre-calculated filter options
  currentFilters: string[]; // Currently selected filter values
  onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  onFilter: (columnId: string, selectedValues: string[]) => void;
}

/**
 * Column Header Component for Contract Manager Tables
 *
 * Features:
 * - Client-side filtering (generates options from originalData)
 * - OR accepts pre-calculated filterOptions (for API-based metadata)
 * - Auto-select all items on first load
 * - Consistent with AuditColumnHeader and AnalyticsColumnHeader patterns
 */
export const ContractManagerColumnHeader: React.FC<ContractManagerColumnHeaderProps> = ({
  column,
  accessorKey,
  customHeader,
  originalData,
  filterOptions,
  currentFilters,
  onSort,
  onFilter,
}) => {
  const { appTour } = useTourContext();
  const tour = appTour.registerTour('rate-card-table');
  // Use provided filterOptions or generate from originalData
  const filterItems = useMemo<TableFilterItem[]>(() => {
    if (filterOptions) return filterOptions;
    if (originalData) return createFilterOptions(originalData, accessorKey) || [];
    return [];
  }, [filterOptions, originalData, accessorKey]);

  // Calculate counter badge (show applied count if partial selection)
  const count = calculateFilterCount(filterItems, currentFilters || []);

  return (
    <div id={tour.registerStep('columnFilters', [], filterItems.length > 0)}>
      <ColumnHeaderProvider
        column={column}
        sort={{
          ascText: 'Sort A to Z',
          descText: 'Sort Z to A',
          onSort,
        }}
        items={{
          items: filterItems,
          isLoading: false,
          count,
        }}
        filter={{
          initialSelectedValues: currentFilters,
          onFilter,
          autoSelect: true, // Auto-select all by default
        }}
      >
        <ColumnHeader customHeader={customHeader} />
      </ColumnHeaderProvider>
    </div>
  );
};
